import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError, withRetry } from "../lib/api";
import { PageShell } from "../components/PageShell";
import { VragenlijstWizard } from "../components/VragenlijstWizard";
import { useLocalStorageState, hasStoredProgress } from "../lib/useLocalStorageState";
import { EMPTY_ANTWOORDEN } from "../lib/antwoordenSchema";

export default function FollowupPage() {
  const { progressie_token } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const weekParam = params.get("week") === "12" ? "12" : "4";
  const meetMoment = weekParam === "12" ? "week_12" : "week_4";
  const storageKey = `motera_followup_${progressie_token}_week${weekParam}`;
  const had = hasStoredProgress(storageKey);
  const [resumeChoice, setResumeChoice] = useState(had ? null : "fresh");
  const qc = useQueryClient();
  const [antwoorden, setAntwoorden, removeAntwoorden] = useLocalStorageState(storageKey, EMPTY_ANTWOORDEN);
  const [apiError, setApiError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const versieQ = useQuery({
    queryKey: ["vragenlijst-actief"],
    queryFn: async () => (await withRetry(() => api.get("/vragenlijst/actief"))).data,
    staleTime: 5 * 60 * 1000,
  });

  const defaultsQ = useQuery({
    queryKey: ["intake-defaults", progressie_token],
    queryFn: async () => (await withRetry(() => api.get(`/werknemer/${progressie_token}/intake-defaults`))).data,
    staleTime: 60 * 60 * 1000,
  });

  function handleDefaultsUpdate(nieuwe) {
    // De zojuist ingevulde geboortedatum meteen als leeftijd in de antwoorden zetten,
    // anders blijft stap 1 klagen over een veld dat er niet is. Zelfde als op de baseline.
    if (nieuwe?.leeftijd) {
      setAntwoorden({ ...antwoorden, leeftijd: nieuwe.leeftijd });
      qc.setQueryData(["intake-defaults", progressie_token], nieuwe);
    }
  }

  // Het intake-eindpunt geeft twee verschillende 404's: geen geboortedatum, of een
  // ongeldige link. Alleen bij de eerste vraagt de wizard erom — bij een verlopen link
  // zou opslaan toch afketsen, en bij een storing bestaat de datum misschien wél en zou
  // hij overschreven worden.
  const geboortedatumOntbreekt =
    defaultsQ.error?.response?.data?.detail?.error_code === "geen_geboortedatum";

  useEffect(() => {
    if (defaultsQ.data?.leeftijd && !antwoorden.leeftijd) {
      setAntwoorden({ ...antwoorden, leeftijd: defaultsQ.data.leeftijd });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultsQ.data?.leeftijd]);

  async function submit(payload) {
    if (!versieQ.data?.versie_id) return;
    setSubmitting(true); setApiError(null);
    try {
      await withRetry(() => api.post(`/werknemer/${progressie_token}/followup`, {
        meet_moment: meetMoment,
        vragenlijst_versie_id: versieQ.data.versie_id,
        antwoorden: payload,
      }));
      removeAntwoorden();
      navigate(`/followup/${progressie_token}/bevestigd?week=${weekParam}`);
    } catch (err) {
      const formatted = formatApiError(err, { schema: versieQ.data?.vragen_json });
      setApiError(formatted);
      if (formatted.kind === "dubbel") {
        removeAntwoorden();
        setTimeout(() => navigate(`/followup/${progressie_token}/bevestigd?week=${weekParam}&al=1`), 1500);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (versieQ.isLoading || defaultsQ.isLoading) return <PageShell><p data-testid="loading-msg" className="text-center text-sm text-slate-500">Vragenlijst laden…</p></PageShell>;
  if (versieQ.error) return <PageShell><p data-testid="error-load" className="text-center text-sm text-red-700">Vragenlijst kon niet geladen worden.</p></PageShell>;

  if (resumeChoice === null) {
    return (
      <PageShell title={`Follow-up week ${weekParam}`}>
        <div data-testid="resume-prompt" className="rounded-lg border border-amber-300 bg-amber-50 p-5">
          <h2 className="text-lg font-semibold">U was al begonnen</h2>
          <p className="mt-2 text-sm text-slate-700">Doorgaan waar u gebleven was, of opnieuw beginnen?</p>
          <div className="mt-4 flex gap-3">
            <button data-testid="btn-resume" onClick={() => setResumeChoice("resume")}
              className="flex-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">Doorgaan</button>
            <button data-testid="btn-restart"
              onClick={() => { removeAntwoorden(); setAntwoorden(EMPTY_ANTWOORDEN); setResumeChoice("fresh"); }}
              className="flex-1 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700">Opnieuw beginnen</button>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell title={`Follow-up week ${weekParam}`}>
      <p className="mb-4 rounded bg-blue-50 px-3 py-2 text-xs text-blue-800">
        Dit is uw {weekParam === "12" ? "12-weken" : "4-weken"} follow-up. Dezelfde vragenlijst als bij uw baseline.
      </p>
      <VragenlijstWizard
        initialAntwoorden={antwoorden}
        onChange={setAntwoorden}
        onSubmit={submit}
        skipAvg={true}
        submitting={submitting}
        apiError={apiError}
        intakeDefaults={defaultsQ.data}
        progressieToken={progressie_token}
        geboortedatumOntbreekt={geboortedatumOntbreekt}
        onDefaultsUpdate={handleDefaultsUpdate}
        schema={versieQ.data?.vragen_json}
        isVervolg={true}
      />
    </PageShell>
  );
}
